/* eslint-disable @typescript-eslint/no-explicit-any */
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import {
  AutoComplete,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import dayjs from "dayjs";
import "dayjs/locale/tr";
import localeData from "dayjs/plugin/localeData";
import weekday from "dayjs/plugin/weekday";
import weekOfYear from "dayjs/plugin/weekOfYear";
import weekYear from "dayjs/plugin/weekYear";
import React, { useCallback, useEffect, useState } from "react";
import { MaterialService } from "../services/MaterialService";
import { GetAllMachineList } from "../services/ReportService";
import {
  ServiceRecordCreateModel,
  ServiceRecordItemCreateModel,
  ServiceRecordItemModel,
  ServiceRecordModel,
  ServiceRecordService,
} from "../services/ServiceRecordService";
import { GetAllUsers } from "../services/UsersService";

// Extend dayjs with plugins
dayjs.extend(localeData);
dayjs.extend(weekday);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);

const { Option } = Select;
const { Title, Text } = Typography;

interface MachineModel {
  serial_number: string;
  model: string;
  customer: string;
  customer_phone?: string;
}

interface Material {
  id: number;
  stockCode: string;
  materialName: string;
  materialSecondName?: string;
  price: number;
  currency: string;
  createdDate: string;
  updatedDate?: string;
  createdBy?: string;
  updatedBy?: string;
}

const ServiceRecordsScreen: React.FC = () => {
  // Set dayjs locale
  dayjs.locale("tr");

  const [loading, setLoading] = useState(false);
  const [serviceRecords, setServiceRecords] = useState<ServiceRecordModel[]>(
    []
  );
  const [materials, setMaterials] = useState<Material[]>([]);
  const [machines, setMachines] = useState<MachineModel[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [materialOptions, setMaterialOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [materialSearchStates, setMaterialSearchStates] = useState<{
    [key: string]: { value: number; label: string }[];
  }>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] =
    useState<ServiceRecordModel | null>(null);
  const [selectedItems, setSelectedItems] = useState<ServiceRecordItemModel[]>(
    []
  );
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });

  // Form states
  const [selectedMaterials, setSelectedMaterials] = useState<
    ServiceRecordItemCreateModel[]
  >([]);
  const [totalAmounts, setTotalAmounts] = useState({
    tl: 0,
    usd: 0,
    eur: 0,
  });

  // Autocomplete states
  const [machineOptions, setMachineOptions] = useState<
    { value: string; label: string }[]
  >([]);

  useEffect(() => {
    loadServiceRecords();
    loadMaterials();
    loadMachines();
    loadAllUsers();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadServiceRecords = async (
    page = 1,
    pageSize = 50,
    searchTerm = "",
    machineSerial = ""
  ) => {
    setLoading(true);
    try {
      const result = await ServiceRecordService.getServiceRecords({
        page,
        pageSize,
        searchTerm,
        machineSerialNumber: machineSerial,
      });

      if (result.success) {
        setServiceRecords(result.data.serviceRecords);
        setPagination({
          current: result.data.page,
          pageSize: result.data.pageSize,
          total: result.data.totalCount,
        });
      } else {
        message.error(result.message);
      }
    } catch (error) {
      message.error("Servis kayıtları yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  const loadAllUsers = async () => {
    try {
      const result = await GetAllUsers();
      if (result) {
        setAllUsers(result);
      } else {
        message.error("Kullanıcılar yüklenirken hata oluştu");
      }
    } catch (error) {
      message.error("Kullanıcılar yüklenirken hata oluştu");
    }
  };

  // Servis kullanıcılarını filtrele
  const serviceUsers = allUsers.filter((user) => user.role === "servis");

  const loadMaterials = useCallback(async () => {
    try {
      const result = await MaterialService.getMaterials({
        page: 1,
        pageSize: 1000000,
      });
      if (result.success) {
        setMaterials(result.data.materials);

        // Create autocomplete options
        const options = result.data.materials.map((material: Material) => ({
          value: material.id,
          label: `${material.stockCode} - ${material.materialName}`,
        }));
        setMaterialOptions(options);

        // Initialize search states for all existing materials
        const newSearchStates: {
          [key: string]: { value: number; label: string }[];
        } = {};
        selectedMaterials.forEach((item) => {
          if (item.id) {
            newSearchStates[item.id] = options;
          }
        });
        setMaterialSearchStates(newSearchStates);
      }
    } catch (error) {
      console.error("Error loading materials:", error);
    }
  }, [selectedMaterials]);

  const loadMachines = async () => {
    try {
      const machinesData = await GetAllMachineList();
      console.log("Machines data:", machinesData);

      // Ensure machinesData is an array
      if (!Array.isArray(machinesData)) {
        console.error("Machines data is not an array:", machinesData);
        setMachines([]);
        setMachineOptions([]);
        return;
      }

      setMachines(machinesData);

      // Create autocomplete options
      const options = machinesData.map((machine: MachineModel) => {
        const parts = [];
        if (machine.serial_number) parts.push(machine.serial_number);
        if (machine.model) parts.push(machine.model);
        if (machine.customer) parts.push(`(${machine.customer})`);

        return {
          value: machine.serial_number || "",
          label: parts.join(" - "),
        };
      });
      setMachineOptions(options);
    } catch (error) {
      console.error("Error loading machines:", error);
      setMachines([]);
      setMachineOptions([]);
    }
  };

  const handleAutocompleteSearch = useCallback(
    (value: string) => {
      if (!value) {
        setMachineOptions(
          machines.map((machine: MachineModel) => {
            const parts = [];
            if (machine.serial_number) parts.push(machine.serial_number);
            if (machine.model) parts.push(machine.model);
            if (machine.customer) parts.push(`(${machine.customer})`);

            return {
              value: machine.serial_number || "",
              label: parts.join(" - "),
            };
          })
        );
        return;
      }

      const filteredOptions = machines
        .filter((machine: MachineModel) => {
          const serialNumber = machine.serial_number?.toLowerCase() || "";
          const model = machine.model?.toLowerCase() || "";
          const customer = machine.customer?.toLowerCase() || "";
          const searchValue = value.toLowerCase();

          return (
            serialNumber.includes(searchValue) ||
            model.includes(searchValue) ||
            customer.includes(searchValue)
          );
        })
        .map((machine: MachineModel) => {
          const parts = [];
          if (machine.serial_number) parts.push(machine.serial_number);
          if (machine.model) parts.push(machine.model);
          if (machine.customer) parts.push(`(${machine.customer})`);

          return {
            value: machine.serial_number || "",
            label: parts.join(" - "),
          };
        });

      setMachineOptions(filteredOptions);
    },
    [machines]
  );

  const handleSearch = () => {
    const values = searchForm.getFieldsValue();
    loadServiceRecords(
      1,
      pagination.pageSize,
      values.searchTerm,
      values.machineSerial
    );
  };

  const handleTableChange = (pagination: any) => {
    const values = searchForm.getFieldsValue();
    loadServiceRecords(
      pagination.current,
      pagination.pageSize,
      values.searchTerm,
      values.machineSerial
    );
  };

  const handleAddMaterial = () => {
    const newId = `material-${Date.now()}-${Math.random()}`;
    setSelectedMaterials([
      ...selectedMaterials,
      {
        id: newId, // Unique ID
        materialId: 0,
        quantity: 1,
        isLabor: false,
        laborPrice: 0,
        laborCurrency: "₺",
      },
    ]);

    // Initialize search state for new material
    setMaterialSearchStates((prev) => ({
      ...prev,
      [newId]: materialOptions,
    }));
  };

  const handleRemoveMaterial = (id: string) => {
    // İşçilik alanını silmeyi engelle
    const materialToRemove = selectedMaterials.find((item) => item.id === id);
    if (materialToRemove?.isLabor) {
      message.warning("İşçilik alanı silinemez!");
      return;
    }

    const newMaterials = selectedMaterials.filter((item) => item.id !== id);
    setSelectedMaterials(newMaterials);
    calculateTotals(newMaterials);

    // Remove search state for removed material
    setMaterialSearchStates((prev) => {
      const newStates = { ...prev };
      delete newStates[id];
      return newStates;
    });
  };

  const handleMaterialChange = (
    id: string,
    field: keyof ServiceRecordItemCreateModel,
    value: any
  ) => {
    const newMaterials = selectedMaterials.map((item) =>
      item.id === id ? { ...item, [field]: value } : item
    );
    setSelectedMaterials(newMaterials);
    calculateTotals(newMaterials);
  };

  const calculateTotals = (
    selectedMaterials: ServiceRecordItemCreateModel[]
  ) => {
    const totals = { tl: 0, usd: 0, eur: 0 };

    selectedMaterials.forEach((item) => {
      // Normal malzeme hesaplaması
      if (!item.isLabor) {
        const material = materials.find((m) => m.id === item.materialId);
        if (material) {
          const total = material.price * item.quantity;
          switch (material.currency) {
            case "₺":
            case "TL":
              totals.tl += total;
              break;
            case "$":
            case "USD":
              totals.usd += total;
              break;
            case "€":
            case "EUR":
              totals.eur += total;
              break;
          }
        }
      } else {
        // İşçilik hesaplaması
        const laborTotal = (item.laborPrice || 0) * item.quantity;
        switch (item.laborCurrency) {
          case "₺":
          case "TL":
            totals.tl += laborTotal;
            break;
          case "$":
          case "USD":
            totals.usd += laborTotal;
            break;
          case "€":
          case "EUR":
            totals.eur += laborTotal;
            break;
        }
      }
    });

    setTotalAmounts(totals);
  };

  const handleCreateRecord = async () => {
    try {
      const values = await form.validateFields();

      const serviceRecord: ServiceRecordCreateModel = {
        machineSerialNumber: values.machineSerialNumber,
        serviceDate: values.serviceDate.format("YYYY-MM-DD"),
        serviceDocumentNo: values.serviceDocumentNo,
        customerName: values.customerName,
        customerPhone: values.customerPhone,
        netAmount: values.netAmount,
        netAmountCurrency: values.netAmountCurrency,
        serviceUserMail: values.serviceUserId,
        items: selectedMaterials.filter(
          (item) => item.materialId > 0 || item.isLabor
        ),
      };

      const result = await ServiceRecordService.createServiceRecord(
        serviceRecord
      );

      if (result.success) {
        message.success("Servis kaydı başarıyla oluşturuldu");
        setIsModalOpen(false);
        form.resetFields();
        setSelectedMaterials([]);
        setTotalAmounts({ tl: 0, usd: 0, eur: 0 });
        loadServiceRecords();
      } else {
        message.error(result.message);
      }
    } catch (error) {
      message.error("Form doğrulama hatası");
    }
  };

  const handleDeleteRecord = async (id: number) => {
    try {
      const result = await ServiceRecordService.deleteServiceRecord(id);
      if (result.success) {
        message.success("Servis kaydı başarıyla silindi");
        loadServiceRecords();
      } else {
        message.error(result.message);
      }
    } catch (error) {
      message.error("Servis kaydı silinirken hata oluştu");
    }
  };

  const handleViewRecord = async (record: ServiceRecordModel) => {
    try {
      const result = await ServiceRecordService.getServiceRecord(record.id);
      if (result.success) {
        setSelectedRecord(record);
        setSelectedItems(result.data.items);
        setIsModalOpen(true);
      } else {
        message.error(result.message);
      }
    } catch (error) {
      message.error("Servis kaydı detayları getirilirken hata oluştu");
    }
  };

  const columns = [
    {
      title: "Kayıt No",
      dataIndex: "recordNo",
      key: "recordNo",
      sorter: true,
    },
    {
      title: "Makina Seri No",
      dataIndex: "machineSerialNumber",
      key: "machineSerialNumber",
      sorter: true,
    },
    {
      title: "Servis Tarihi",
      dataIndex: "serviceDate",
      key: "serviceDate",
      render: (date: string) => dayjs(date).format("DD.MM.YYYY"),
      sorter: true,
    },
    {
      title: "Servis Evrak No",
      dataIndex: "serviceDocumentNo",
      key: "serviceDocumentNo",
      sorter: true,
    },
    {
      title: "Müşteri Adı",
      dataIndex: "customerName",
      key: "customerName",
      sorter: true,
    },
    {
      title: "Müşteri Telefonu",
      dataIndex: "customerPhone",
      key: "customerPhone",
      sorter: true,
    },
    {
      title: "Net Tutar",
      dataIndex: "netAmount",
      key: "netAmount",
      render: (amount: number, record: ServiceRecordModel) =>
        `${amount.toLocaleString("tr-TR")} ${record.netAmountCurrency}`,
      sorter: true,
    },
    {
      title: "Servis Alan",
      dataIndex: "createdBy",
      key: "createdBy",
      sorter: true,
    },
    {
      title: "Kur",
      dataIndex: "exchangeRate",
      key: "exchangeRate",
      render: (rate: number) => rate.toFixed(4),
      sorter: true,
    },
    {
      title: "Oluşturma Tarihi",
      dataIndex: "createdDate",
      key: "createdDate",
      render: (date: string) => dayjs(date).format("DD.MM.YYYY HH:mm"),
      sorter: true,
    },
    {
      title: "İşlemler",
      key: "actions",
      render: (_: any, record: ServiceRecordModel) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleViewRecord(record)}
          >
            Görüntüle
          </Button>
          <Popconfirm
            title="Bu servis kaydını silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDeleteRecord(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Sil
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Card>
        <Title level={3}>Servis Kayıtları</Title>

        {/* Search Form */}
        <Form
          form={searchForm}
          layout="inline"
          onFinish={handleSearch}
          style={{ marginBottom: "16px" }}
        >
          <Form.Item name="searchTerm">
            <Input placeholder="Kayıt no ara" style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="machineSerial">
            <AutoComplete
              placeholder="Makina seç ve ara"
              style={{ width: 300 }}
              options={machineOptions}
              onSearch={handleAutocompleteSearch}
              onSelect={(value: string) => {
                searchForm.setFieldsValue({ machineSerial: value });
              }}
              allowClear
              filterOption={false}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Ara
            </Button>
          </Form.Item>
        </Form>

        <div style={{ marginBottom: "16px", textAlign: "right" }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setSelectedRecord(null);
              setSelectedItems([]);
              // İşçilik alanını otomatik ekle
              const laborId = "labor-default";
              setSelectedMaterials([
                {
                  id: laborId,
                  materialId: 0,
                  quantity: 1,
                  isLabor: true,
                  laborPrice: 0,
                  laborCurrency: "₺",
                },
              ]);
              setIsModalOpen(true);
            }}
          >
            Yeni Servis Kaydı
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={serviceRecords}
          loading={loading}
          rowKey="id"
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} / ${total} kayıt`,
          }}
          onChange={handleTableChange}
        />
      </Card>

      {/* Create/View Modal */}
      <Modal
        title={selectedRecord ? "Servis Kaydı Detayları" : "Yeni Servis Kaydı"}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
          setSelectedMaterials([]);
          setTotalAmounts({ tl: 0, usd: 0, eur: 0 });
        }}
        width={800}
        footer={
          selectedRecord
            ? [
                <Button key="close" onClick={() => setIsModalOpen(false)}>
                  Kapat
                </Button>,
              ]
            : [
                <Button key="cancel" onClick={() => setIsModalOpen(false)}>
                  İptal
                </Button>,
                <Button
                  key="submit"
                  type="primary"
                  onClick={handleCreateRecord}
                >
                  Kaydet
                </Button>,
              ]
        }
      >
        {selectedRecord ? (
          // View Mode
          <div>
            <Row gutter={16}>
              <Col span={12}>
                <Text strong>Kayıt No:</Text> {selectedRecord.recordNo}
              </Col>
              <Col span={12}>
                <Text strong>Makina Seri No:</Text>{" "}
                {selectedRecord.machineSerialNumber}
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: "8px" }}>
              <Col span={12}>
                <Text strong>Servis Tarihi:</Text>{" "}
                {dayjs(selectedRecord.serviceDate).format("DD.MM.YYYY")}
              </Col>
              <Col span={12}>
                <Text strong>Servis Evrak No:</Text>{" "}
                {selectedRecord.serviceDocumentNo || "-"}
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: "8px" }}>
              <Col span={12}>
                <Text strong>Müşteri Adı:</Text>{" "}
                {selectedRecord.customerName || "-"}
              </Col>
              <Col span={12}>
                <Text strong>Müşteri Telefon:</Text>{" "}
                {selectedRecord.customerPhone || "-"}
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: "8px" }}>
              <Col span={12}>
                <Text strong>Net Tutar:</Text>{" "}
                {selectedRecord.netAmount.toLocaleString("tr-TR")}{" "}
                {selectedRecord.netAmountCurrency}
              </Col>
              <Col span={12}>
                <Text strong>Oluşturma Tarihi:</Text>{" "}
                {dayjs(selectedRecord.createdDate).format("DD.MM.YYYY HH:mm")}
              </Col>
            </Row>

            <Row gutter={16} style={{ marginTop: "8px" }}>
              <Col span={12}>
                <Text strong>Servis Alan:</Text>{" "}
                {(() => {
                  const serviceUser = serviceUsers.find(
                    (user) => user.mail === selectedRecord?.createdBy
                  );
                  return serviceUser
                    ? `${serviceUser.name} ${serviceUser.surname} (${serviceUser.mail})`
                    : selectedRecord?.createdBy || "-";
                })()}
              </Col>
            </Row>

            <Divider />

            <Title level={5}>Malzemeler</Title>
            <Table
              dataSource={selectedItems}
              columns={[
                {
                  title: "Stok Kodu",
                  dataIndex: "materialStockCode",
                  key: "materialStockCode",
                },
                {
                  title: "Malzeme Adı",
                  dataIndex: "materialName",
                  key: "materialName",
                },
                {
                  title: "Birim Fiyat",
                  dataIndex: "materialPrice",
                  key: "materialPrice",
                  render: (price, record) => {
                    if (record.isLabor) {
                      return `${(record.laborPrice || 0).toLocaleString(
                        "tr-TR"
                      )} ${record.laborCurrency || "₺"}`;
                    }
                    return `${price.toLocaleString("tr-TR")} ${
                      record.materialCurrency
                    }`;
                  },
                },
                { title: "Adet", dataIndex: "quantity", key: "quantity" },
                {
                  title: "Toplam",
                  dataIndex: "totalAmount",
                  key: "totalAmount",
                  render: (amount, record) => {
                    if (record.isLabor) {
                      const total = (record.laborPrice || 0) * record.quantity;
                      return `${total.toLocaleString("tr-TR")} ${
                        record.laborCurrency || "₺"
                      }`;
                    }
                    return `${amount.toLocaleString("tr-TR")} ${
                      record.materialCurrency
                    }`;
                  },
                },
              ]}
              pagination={false}
              size="small"
            />
          </div>
        ) : (
          // Create Mode
          <Form form={form} layout="vertical">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="machineSerialNumber"
                  label="Makina Seri No"
                  rules={[
                    { required: true, message: "Makina seri no giriniz" },
                  ]}
                >
                  <AutoComplete
                    placeholder="Makina seri no seçin veya girin"
                    options={machineOptions}
                    onSearch={handleAutocompleteSearch}
                    onSelect={(value: string) => {
                      form.setFieldsValue({ machineSerialNumber: value });
                      // Auto-fill customer information if available
                      const selectedMachine = machines.find(
                        (m) => m.serial_number === value
                      );
                      if (selectedMachine) {
                        form.setFieldsValue({
                          customerName: selectedMachine.customer || "",
                          customerPhone: selectedMachine.customer_phone || "",
                        });
                      }
                    }}
                    allowClear
                    filterOption={false}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="serviceDate"
                  label="Servis Tarihi"
                  rules={[{ required: true, message: "Servis tarihi seçiniz" }]}
                  initialValue={dayjs()}
                >
                  {/* @ts-ignore */}
                  <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="serviceDocumentNo" label="Servis Evrak No">
                  <Input placeholder="Servis evrak no (opsiyonel)" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="customerName" label="Müşteri Adı">
                  <Input placeholder="Müşteri adı (opsiyonel)" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="customerPhone" label="Müşteri Telefon No">
                  <Input placeholder="Müşteri telefon no (opsiyonel)" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="serviceUserId"
                  label="Servis Kullanıcısı"
                  rules={[
                    { required: true, message: "Servis kullanıcısı seçiniz" },
                  ]}
                >
                  <Select placeholder="Servis kullanıcısı seçin">
                    {serviceUsers.map((user, index) => (
                      <Option key={index} value={user.mail}>
                        {user.name} {user.surname} ({user.mail})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Title level={5}>Malzemeler</Title>
            {selectedMaterials.map((item) => (
              <Card key={item.id} size="small" style={{ marginBottom: "8px" }}>
                <Row gutter={8} align="middle">
                  {item.isLabor ? (
                    // İşçilik alanı için özel UI
                    <>
                      <Col span={6}>
                        <Text strong style={{ color: "#1890ff" }}>
                          İşçilik
                        </Text>
                      </Col>
                      <Col span={4}>
                        <InputNumber
                          placeholder="Fiyat"
                          value={item.laborPrice}
                          onChange={(value) =>
                            handleMaterialChange(
                              item.id!,
                              "laborPrice",
                              value || 0
                            )
                          }
                          min={0}
                          style={{ width: "100%" }}
                        />
                      </Col>
                      <Col span={4}>
                        <Select
                          value={item.laborCurrency}
                          onChange={(value) =>
                            handleMaterialChange(
                              item.id!,
                              "laborCurrency",
                              value
                            )
                          }
                          style={{ width: "100%" }}
                        >
                          <Option value="₺">₺</Option>
                          <Option value="$">$</Option>
                          <Option value="€">€</Option>
                        </Select>
                      </Col>
                      <Col span={4}>
                        <InputNumber
                          placeholder="Adet"
                          value={item.quantity}
                          onChange={(value) =>
                            handleMaterialChange(
                              item.id!,
                              "quantity",
                              value || 1
                            )
                          }
                          min={1}
                          style={{ width: "100%" }}
                        />
                      </Col>
                      <Col span={4}>
                        <Text>
                          {item.laborPrice && item.quantity
                            ? `${(
                                item.laborPrice * item.quantity
                              ).toLocaleString("tr-TR")} ${item.laborCurrency}`
                            : "0 ₺"}
                        </Text>
                      </Col>
                      <Col span={2}>
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleRemoveMaterial(item.id!)}
                          disabled={true}
                          style={{ opacity: 0.3 }}
                        />
                      </Col>
                    </>
                  ) : (
                    // Normal malzeme UI
                    <>
                      <Col span={10}>
                        <AutoComplete
                          placeholder="Malzeme seçiniz veya arayın"
                          options={
                            materialSearchStates[item.id!] || materialOptions
                          }
                          value={
                            item.materialId
                              ? (
                                  materialSearchStates[item.id!] ||
                                  materialOptions
                                ).find((opt) => opt.value === item.materialId)
                                  ?.label
                              : undefined
                          }
                          onSelect={(value: string, option: any) => {
                            handleMaterialChange(
                              item.id!,
                              "materialId",
                              option.value
                            );
                          }}
                          onSearch={(searchValue: string) => {
                            if (!searchValue) {
                              setMaterialSearchStates((prev) => ({
                                ...prev,
                                [item.id!]: materialOptions,
                              }));
                              return;
                            }

                            const filteredOptions = materials
                              .filter((material: Material) => {
                                const stockCode =
                                  material.stockCode?.toLowerCase() || "";
                                const materialName =
                                  material.materialName?.toLowerCase() || "";
                                const searchLower = searchValue.toLowerCase();

                                return (
                                  stockCode.includes(searchLower) ||
                                  materialName.includes(searchLower)
                                );
                              })
                              .map((material: Material) => ({
                                value: material.id,
                                label: `${material.stockCode} - ${material.materialName}`,
                              }));

                            setMaterialSearchStates((prev) => ({
                              ...prev,
                              [item.id!]: filteredOptions,
                            }));
                          }}
                          allowClear
                          filterOption={false}
                          style={{ width: "100%" }}
                        />
                      </Col>
                      <Col span={6}>
                        <InputNumber
                          placeholder="Adet"
                          value={item.quantity}
                          onChange={(value) =>
                            handleMaterialChange(
                              item.id!,
                              "quantity",
                              value || 1
                            )
                          }
                          min={1}
                          style={{ width: "100%" }}
                        />
                      </Col>
                      <Col span={6}>
                        <Text>
                          {item.materialId > 0 &&
                            (() => {
                              const material = materials.find(
                                (m) => m.id === item.materialId
                              );
                              return material
                                ? `${(
                                    material.price * item.quantity
                                  ).toLocaleString("tr-TR")} ${
                                    material.currency
                                  }`
                                : "";
                            })()}
                        </Text>
                      </Col>
                      <Col span={2}>
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleRemoveMaterial(item.id!)}
                        />
                      </Col>
                    </>
                  )}
                </Row>
              </Card>
            ))}

            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={handleAddMaterial}
              style={{ width: "100%", marginBottom: "16px" }}
            >
              Malzeme Ekle
            </Button>

            <Row gutter={16}>
              <Col span={8}>
                <Text strong>
                  Toplam TL: {totalAmounts.tl.toLocaleString("tr-TR")} ₺
                </Text>
              </Col>
              <Col span={8}>
                <Text strong>
                  Toplam USD: {totalAmounts.usd.toLocaleString("tr-TR")} $
                </Text>
              </Col>
              <Col span={8}>
                <Text strong>
                  Toplam EUR: {totalAmounts.eur.toLocaleString("tr-TR")} €
                </Text>
              </Col>
            </Row>

            <Divider />

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="netAmount"
                  label="Net Tutar"
                  rules={[{ required: true, message: "Net tutar giriniz" }]}
                >
                  <InputNumber
                    placeholder="Net tutar"
                    style={{ width: "100%" }}
                    formatter={(value) =>
                      `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }
                    parser={(value) => value!.replace(/\$\s?|(,*)/g, "")}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="netAmountCurrency"
                  label="Para Birimi"
                  rules={[{ required: true, message: "Para birimi seçiniz" }]}
                >
                  <Select placeholder="Para birimi">
                    <Option value="₺">₺</Option>
                    <Option value="$">$</Option>
                    <Option value="€">€</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default ServiceRecordsScreen;
